import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import {
  CART_DRAWER_SECTION_ID,
  delayCartRequest,
  fetchCartWithTimeout,
  getCartMutationSequence,
  getCartRetryDelay,
  getPendingCartMutation,
} from '@theme/sh-cart-service';

const CART_STATE_MANAGER_KEY = 'SecretHourCartState';
const CART_DRAWER_OPEN_WAIT_MS = 700;
const CART_DRAWER_REFRESH_TIMEOUT_MS = 6000;
const CART_DRAWER_RETRYABLE_STATUS_CODES = new Set([408, 425, 429, 500, 502, 503, 504]);
const CART_DRAWER_ADD_SOURCES = new Set(['product-form-component', 'sh-addon-quick-add', 'quick-order-quantity']);

function getControlBaseDisabled(control, ownLockKey) {
  for (const key of [
    'cartPendingDisabled',
    'cartMutationDisabled',
    'cartSectionDisabled',
    'cartRefreshDisabled',
  ]) {
    if (key !== ownLockKey && control.dataset[key] !== undefined) return control.dataset[key];
  }

  return control.disabled ? 'true' : 'false';
}

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {Promise<boolean> | null} */
  #drawerRefreshPromise = null;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  #autoOpenTimer;
  #autoOpenPending = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    window.clearTimeout(this.#autoOpenTimer);
    this.#autoOpenPending = false;
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (!(event instanceof CartAddEvent)) return;

    const data = event.detail?.data || {};
    if (!CART_DRAWER_ADD_SOURCES.has(data.source)) return;
    if (data.optimistic || data.didError || data.recovery) return;

    const cartCount = Number(event.detail.resource?.item_count ?? data.itemCount);
    if (!Number.isFinite(cartCount) || cartCount <= 0) return;

    if (this.hasAttribute('auto-open')) {
      const refreshPromise = this.#refreshStaleDrawerIfNeeded();
      const renderPromise = refreshPromise || (data.sections ? Promise.resolve() : null);
      this.#scheduleAutoOpen(renderPromise, cartCount);
      return;
    }

    if (this.refs.dialog?.open) this.#refreshStaleDrawerIfNeeded();
    this.#announceCartCount(cartCount);
  };

  #scheduleAutoOpen(refreshPromise, cartCount) {
    if (this.refs.dialog?.open) {
      this.#announceCartCount(cartCount);
      return;
    }
    if (this.#autoOpenPending) return;

    this.#autoOpenPending = true;
    const show = () => {
      if (!this.#autoOpenPending) return;
      this.#autoOpenPending = false;
      window.clearTimeout(this.#autoOpenTimer);
      if (!this.isConnected || this.refs.dialog?.open) {
        this.#announceCartCount(cartCount);
        return;
      }
      this.showDialog();
      this.#announceCartCount(cartCount);
    };

    this.#autoOpenTimer = window.setTimeout(show, CART_DRAWER_OPEN_WAIT_MS);
    if (refreshPromise) {
      refreshPromise.finally(show);
    } else {
      show();
    }
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.#refreshStaleDrawerIfNeeded();
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  #refreshStaleDrawerIfNeeded() {
    if (!this.#shouldRefreshStaleDrawer()) return null;
    if (this.#drawerRefreshPromise) return this.#drawerRefreshPromise;

    this.#setDrawerRefreshBusy(true);
    const pendingMutation = getPendingCartMutation();
    this.#drawerRefreshPromise = Promise.resolve(pendingMutation)
      .catch(() => undefined)
      .then(() => this.#refreshDrawerContent())
      .then((didRefresh) => {
        if (didRefresh) this.#clearDrawerStaleState();
        return didRefresh;
      })
      .catch((error) => {
        console.warn('[Secret Hour Cart Drawer] Unable to refresh stale drawer:', error);
        return false;
      })
      .finally(() => {
        this.#setDrawerRefreshBusy(false);
        this.#drawerRefreshPromise = null;
      });

    return this.#drawerRefreshPromise;
  }

  #shouldRefreshStaleDrawer() {
    const latestCart = this.#getLatestCart();
    const latestCount = Number(latestCart?.item_count);
    const headerCount = this.#getHeaderCartCount();
    const hasPositiveCart = latestCount > 0 || headerCount > 0;

    if (this.#isDrawerMarkedStale()) return true;

    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return false;

    const hasRows = Boolean(dialog.querySelector('.cart-items__table-row[data-key]'));
    const hasCartForm = Boolean(dialog.querySelector('.cart-form'));
    const showsEmptyHeading = Boolean(dialog.querySelector('#cart-drawer-heading-empty, .cart-drawer__heading--empty'));

    if (!hasPositiveCart) return hasRows || hasCartForm || !showsEmptyHeading;

    return !hasRows || !hasCartForm || showsEmptyHeading || dialog.classList.contains('cart-drawer--empty');
  }

  async #refreshDrawerContent() {
    const initialComponent = this.querySelector('cart-items-component[data-drawer][data-section-id]');
    const sectionId = CART_DRAWER_SECTION_ID;
    if (!initialComponent || !sectionId) return false;

    const requestUrl = new URL(window.location.href);
    requestUrl.searchParams.set('sections', sectionId);
    const requestSequence = getCartMutationSequence();

    for (let attempt = 0; attempt < 2; attempt += 1) {
      let shouldRetry = false;
      let retrySource;

      try {
        const response = await fetchCartWithTimeout(
          requestUrl.toString(),
          {
            cache: 'no-store',
            headers: {
              Accept: 'text/html',
              'X-Requested-With': 'XMLHttpRequest',
            },
          },
          CART_DRAWER_REFRESH_TIMEOUT_MS
        );
        retrySource = response;

        if (!response.ok) {
          shouldRetry = attempt === 0 && CART_DRAWER_RETRYABLE_STATUS_CODES.has(response.status);
          if (!shouldRetry) return false;
        } else {
          let sections;
          try {
            sections = await response.json();
          } catch (error) {
            console.warn('[Secret Hour Cart Drawer] Section response was not valid JSON:', error);
            return false;
          }

          const sectionHtml = sections?.[sectionId];
          if (typeof sectionHtml !== 'string' || sectionHtml.trim() === '') {
            console.warn('[Secret Hour Cart Drawer] Section response was empty.');
            return false;
          }

          const fragment = new DOMParser().parseFromString(sectionHtml, 'text/html');
          const escapedSectionId = window.CSS?.escape ? window.CSS.escape(sectionId) : sectionId;
          const nextComponent =
            fragment.querySelector(`cart-items-component[data-drawer][data-section-id="${escapedSectionId}"]`) ||
            fragment.querySelector('cart-items-component[data-drawer]');
          if (!(nextComponent instanceof HTMLElement)) {
            console.warn('[Secret Hour Cart Drawer] Section response did not include cart drawer content.');
            return false;
          }

          const currentComponent = this.querySelector(
            `cart-items-component[data-drawer][data-section-id="${escapedSectionId}"]`
          );
          if (!(currentComponent instanceof HTMLElement) || !currentComponent.isConnected) return false;

          const cartState = window[CART_STATE_MANAGER_KEY];
          if (cartState?.isFlushing || cartState?.pendingUpdates?.size) return false;
          if (getPendingCartMutation() || requestSequence !== getCartMutationSequence()) return false;

          morph(currentComponent, nextComponent, {
            ...MORPH_OPTIONS,
            childrenOnly: false,
            hydrationMode: false,
          });
          this.#syncDrawerEmptyState();
          this.#updateStickyState();
          return true;
        }
      } catch (error) {
        const isTransientTransportError = error?.name === 'TimeoutError' || error instanceof TypeError;
        shouldRetry = attempt === 0 && isTransientTransportError;
        if (!shouldRetry) {
          console.warn('[Secret Hour Cart Drawer] Section refresh failed:', error);
          return false;
        }
      }

      if (shouldRetry) await this.#delay(getCartRetryDelay(retrySource, attempt));
    }

    return false;
  }

  #setDrawerRefreshBusy(busy) {
    const component = this.querySelector('cart-items-component[data-drawer]');
    if (!(component instanceof HTMLElement)) return;

    component.toggleAttribute('data-cart-refreshing', busy);
    component.querySelectorAll('button, input, select, textarea').forEach((control) => {
      if (!(control instanceof HTMLButtonElement || control instanceof HTMLInputElement)) return;
      if (control.matches('[ref="closeButton"], .cart-drawer__close-button')) return;

      if (busy) {
        if (!control.dataset.cartRefreshDisabled) {
          control.dataset.cartRefreshDisabled = getControlBaseDisabled(control, 'cartRefreshDisabled');
        }
        control.disabled = true;
      } else if (control.dataset.cartRefreshDisabled) {
        const remainsLocked =
          component.hasAttribute('data-cart-mutating') || component.hasAttribute('data-cart-section-refreshing');
        control.disabled = remainsLocked || control.dataset.cartRefreshDisabled === 'true';
        delete control.dataset.cartRefreshDisabled;
      }
    });

    const remainsBusy =
      busy || component.hasAttribute('data-cart-mutating') || component.hasAttribute('data-cart-section-refreshing');
    component.toggleAttribute('aria-busy', remainsBusy);
    if (!busy) window[CART_STATE_MANAGER_KEY]?.refreshUnlockedControlStates?.();
  }

  #delay(ms) {
    return delayCartRequest(ms);
  }

  #getLatestCart() {
    const cartState = window[CART_STATE_MANAGER_KEY];
    return cartState?.latestCart || cartState?.confirmedCart || window.SecretHourLatestCart || null;
  }

  #isDrawerMarkedStale() {
    const cartState = window[CART_STATE_MANAGER_KEY];
    return Boolean(window.SecretHourCartDrawerNeedsRefresh || cartState?.drawerNeedsRefresh);
  }

  #clearDrawerStaleState() {
    window.SecretHourCartDrawerNeedsRefresh = false;
    const cartState = window[CART_STATE_MANAGER_KEY];
    if (cartState && typeof cartState === 'object') {
      cartState.drawerNeedsRefresh = false;
    }
  }

  #getHeaderCartCount() {
    const countText = document.querySelector('.cart-bubble__text-count')?.textContent || '';
    const count = Number((countText.match(/\d+/) || [])[0]);
    return Number.isFinite(count) ? count : 0;
  }

  #syncDrawerEmptyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const hasCartForm = Boolean(dialog.querySelector('.cart-form'));
    dialog.classList.toggle('cart-drawer--empty', !hasCartForm);
    dialog.setAttribute('aria-labelledby', hasCartForm ? 'cart-drawer-heading' : 'cart-drawer-heading-empty');
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
